import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { NumberField } from '../../components/form';
import DropDown from '../../components/form/dropdown';
import { post } from '../../libs/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import {
  truncateAmount,
  showPrice,
  showPercent,
  ONE_DAY_PS_PRODUCT_ID,
} from '../../libs/util';
import { truncateCurrencyAmount } from '../../libs/currencyPrecision';
import { groupInteger } from '../../libs/numberFormat';

// Constants
const PAYOUT_SCHEDULES = {
  END_OF_TENURE: 'ON_MATURITY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QTR',
  CUSTOM: 'CUSTOM',
};

const formatDateForAPI = (dateInput) => {
  try {
    if (dateInput instanceof Date) {
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      const hours = String(dateInput.getHours()).padStart(2, '0');
      const minutes = String(dateInput.getMinutes()).padStart(2, '0');
      // Backend expects yyyy-MM-dd HH:mm format (no seconds)
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    if (
      typeof dateInput === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateInput)
    ) {
      return `${dateInput} 08:00:00`;
    }

    if (
      typeof dateInput === 'string' &&
      /^\d{2}-\d{2}-\d{4}$/.test(dateInput)
    ) {
      const [day, month, year] = dateInput.split('-');
      return `${year}-${month}-${day} 08:00:00`;
    }

    const d = new Date(dateInput);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds() || 0).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    console.error('Could not format date for API:', dateInput);
    return dateInput;
  } catch (error) {
    console.error('Error formatting date for API:', error);
    return dateInput;
  }
};

// Update the calculatePayout function to handle errors better
const calculatePayout = async (
  maturityDate,
  investAmount,
  investCcy,
  rate,
  payoutType,
  payoutDates,
  productType = 'CASH_CARRY',
  referenceRate,
  productId,
  notionalCcy
) => {
  try {
    // Format maturity date for API
    const formattedMaturityDate = formatDateForAPI(maturityDate);

    // Send the notional at the precision it is actually booked at — the same
    // number the order form displays and submits. Posting the raw product let
    // the API derive the return from digits that never reach the trade record,
    // so the booked return could land a cent above the displayed notional.
    const notionalAmount = truncateCurrencyAmount(
      investAmount * referenceRate,
      notionalCcy || investCcy
    );

    // Prepare payload
    const payload = {
      maturity_date_time: formattedMaturityDate,
      invest_amount: investAmount,
      invest_ccy: investCcy,
      desired_fixed_rate: rate,
      payout_type: payoutType,
      payout_dates: payoutDates.map((date) => ({
        // date: date.date.split('-').reverse().join(''), // Convert YYYY-MM-DD to DDMMYYYY
        // payout_amount: 0.0, // API expects this field but calculates it
        // is_last_payout: date.isMaturity
        date: formatDateForAPI(date.date),
        payout_amount: 0.0, // API expects this field but BE calculates it
        is_last_payout: date.isMaturity,
      })),
      reference_rate: referenceRate,
      notional_amount: notionalAmount,
      product_id: productId,
    };

    let product_type = 'cc_order';

    if (productType === 'PSL') {
      product_type = 'psl_order';
    } else if (productId === ONE_DAY_PS_PRODUCT_ID) {
      product_type = 'ps_1d_order';
    }

    const url = `/investment/${product_type}/calculate_payout`;
    const response = await post(url, payload);

    // Check for both success codes (0 and 200)
    if ((response.code === 0 || response.code === 200) && response.data) {
      // If response is successful but message is "Successful", return the data
      if (response.msg === 'Successful') {
        return response.data;
      }
      return response.data;
    }

    // If API returns an error message
    if (response.msg) {
      throw new Error(response.msg);
    }

    // If we get here, something went wrong
    throw new Error('Failed to calculate payout');
  } catch (error) {
    console.error('Error calculating payout:', error);
  }
};

const apiCalculatePayout = calculatePayout;

// Helper function to format date for API
const formatDateToAPI = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}${month}${year}`;
};
// Update the MemoizedDatePicker component to filter out weekends
const MemoizedDatePicker = React.memo(
  ({
    selected,
    onChange,
    dateFormat,
    minDate,
    maxDate,
    disabled,
    index,
    ...props
  }) => {
    // Parse date string to Date object
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;

      try {
        // Handle YYYY-MM-DD format
        if (
          typeof dateStr === 'string' &&
          dateStr.match(/^\d{4}-\d{2}-\d{2}$/)
        ) {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
        }

        // Fallback to standard Date constructor
        return new Date(dateStr);
      } catch (e) {
        console.error('Error parsing date:', e);
        return new Date(); // Return current date as fallback
      }
    };

    // Parse the selected date
    const parsedDate = useMemo(() => parseDate(selected), [selected]);

    // Handle date changes
    const handleChange = useCallback(
      (date) => {
        onChange(date);
      },
      [onChange]
    );

    // Parse min and max dates if they exist
    const parsedMinDate = useMemo(
      () => (minDate ? parseDate(minDate) : undefined),
      [minDate]
    );

    // For maxDate, ensure we're using the maturity date
    const parsedMaxDate = useMemo(() => {
      if (!maxDate) return undefined;

      // If maxDate is a boolean (for isMaturity), use the selected date
      if (typeof maxDate === 'boolean') {
        return parsedDate;
      }

      return parseDate(maxDate);
    }, [maxDate, parsedDate]);

    // Add a filter function to exclude weekends
    const filterWeekends = useCallback((date) => {
      const day = date.getDay();
      // Return true for weekdays (Monday-Friday), false for weekends
      return day !== 0 && day !== 6;
    }, []);

    // Holiday Dates (disabled in calendar)
    const HOLIDAYS = [
      // 2025
      new Date(2025, 0, 1), // New Year's Day
      new Date(2025, 0, 29), // Chinese New Year
      new Date(2025, 0, 30),
      new Date(2025, 2, 31), // Hari Raya Puasa
      new Date(2025, 3, 18), // Good Friday
      new Date(2025, 4, 1), // Labour Day
      new Date(2025, 4, 12), // Vesak Day
      new Date(2025, 5, 7), // Hari Raya Haji
      new Date(2025, 7, 9), // National Day
      new Date(2025, 9, 20), // Deepavali
      new Date(2025, 11, 25), // Christmas
      // 2026
      new Date(2026, 0, 1), // New Year's Day
      new Date(2026, 1, 17), // Chinese New Year
      new Date(2026, 1, 18), // Chinese New Year Holiday
      new Date(2026, 3, 3), // Good Friday
      new Date(2026, 4, 1), // Labour Day
      new Date(2026, 4, 27), // Hari Raya Haji
      new Date(2026, 4, 31), // Vesak Day
      new Date(2026, 7, 10), // National Day (observed Mon 10 Aug; actual 9 Aug falls on Sunday)
      new Date(2026, 10, 7), // Deepavali
      new Date(2026, 11, 25), // Christmas
      // 2027
      new Date(2027, 0, 1), // New Year's Day
      new Date(2027, 1, 6), // Chinese New Year
      new Date(2027, 1, 8), // Chinese New Year Holiday
      new Date(2027, 2, 10), // Hari Raya Puasa
      new Date(2027, 2, 26), // Good Friday
      new Date(2027, 4, 1), // Labour Day
      new Date(2027, 4, 17), // Hari Raya Haji
      new Date(2027, 4, 20), // Vesak Day
      new Date(2027, 7, 9), // National Day
      new Date(2027, 9, 28), // Deepavali
      new Date(2027, 11, 25), // Christmas
      // 2028
      new Date(2028, 0, 1), // New Year's Day
      new Date(2028, 0, 26), // Chinese New Year
      new Date(2028, 0, 27), // Chinese New Year Holiday
      new Date(2028, 3, 14), // Good Friday
      new Date(2028, 4, 1), // Labour Day
      new Date(2028, 7, 9), // National Day
      new Date(2028, 11, 25), // Christmas
    ];

    const filterValidDates = useCallback(
      (date) => {
        const day = date.getDay();
        // Block weekends
        const isWeekend = day === 0 || day === 6;

        // Block holidays
        const isHoliday = HOLIDAYS.some(
          (holiday) =>
            holiday.getFullYear() === date.getFullYear() &&
            holiday.getMonth() === date.getMonth() &&
            holiday.getDate() === date.getDate()
        );

        // Allow only non-weekend and non-holiday days
        const isBeforeEffective =
          props.effectiveDate && date <= new Date(props.effectiveDate);

        return !isWeekend && !isHoliday && !isBeforeEffective;
      },
      [props.effectiveDate]
    );

    return (
      <DatePicker
        selected={parsedDate}
        onChange={handleChange}
        dateFormat={dateFormat}
        minDate={parsedMinDate}
        maxDate={parsedMaxDate}
        disabled={disabled}
        className="form-control date-picker"
        filterDate={(date) => filterValidDates(date)}
        {...props}
      />
    );
  }
);

// Wrap the entire component with React.memo with a custom comparison function
const MultiInterestPayout = React.memo(
  ({
    product,
    currency,
    expiredAmount,
    amount,
    daysLeft,
    productAPR,
    referenceRate,
    selectedCurrency,
    onPayoutChange,
    maturity,
    notionalCurrency,
  }) => {
    // State hooks
    const [payoutSchedule, setPayoutSchedule] = useState(
      PAYOUT_SCHEDULES.END_OF_TENURE
    );
    const [payoutOptions, setPayoutOptions] = useState([]);
    const [debouncedAmount, setDebouncedAmount] = useState(amount);
    const [showMonthlyOptions, setShowMonthlyOptions] = useState(false);
    const [showCustomOptions, setShowCustomOptions] = useState(false);
    const [lastProductId, setLastProductId] = useState(null);
    const [workingDays, setWorkingDays] = useState({});
    const [errors, setErrors] = useState([]);
    const [apiTotalPayout, setApiTotalPayout] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [productType, setProductType] = useState('CASH_CARRY');
    const [effectiveDate, setEffectiveDate] = useState(null);

    // Add a ref to store the original dates
    const originalDatesRef = useRef([]);

    // Store the current payoutOptions in a ref to compare with new props
    const prevPayoutOptionsRef = useRef(payoutOptions);

    useEffect(() => {
      if (!amount) {
        setDebouncedAmount(amount);
        return;
      }

      const timer = setTimeout(() => {
        setDebouncedAmount(amount);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }, [amount]);

    useEffect(() => {
      prevPayoutOptionsRef.current = payoutOptions;
      originalDatesRef.current = payoutOptions.map((opt) => opt.date);
    }, [payoutOptions]);

    useEffect(() => {
      const numericAmount = parseFloat(amount);
      const numericDebouncedAmount = parseFloat(debouncedAmount);

      if (
        product &&
        maturity?.endDateTime &&
        daysLeft !== undefined &&
        daysLeft !== null &&
        daysLeft >= 0 &&
        productAPR &&
        numericDebouncedAmount > 0 &&
        payoutSchedule
      ) {
        if (numericAmount > 0) {
          // If we have existing custom payout options and only the rate changed,
          // just recalculate amounts instead of regenerating everything
          if (payoutSchedule === 'CUSTOM' && payoutOptions.length > 0) {
            recalculatePayoutAmounts(payoutOptions);
          } else {
            // For non-custom schedules or when no options exist, generate new options
            const currentSchedule =
              payoutSchedule || PAYOUT_SCHEDULES.END_OF_TENURE;
            generatePayoutOptions(currentSchedule);
          }
        }
      }
    }, [
      product,
      maturity?.endDateTime,
      daysLeft,
      productAPR,
      debouncedAmount,
      payoutSchedule,
      referenceRate,
      amount,
    ]);

    const handleDateChange = useCallback(
      (index, date) => {
        if (!date) return;

        const maturityDate = maturity ? new Date(maturity.endDateTime) : null;

        if (maturityDate && date >= maturityDate) {
          date = new Date(maturityDate);
          date.setDate(date.getDate() - 1);
        }

        // Format the date
        const formattedDate = formatYMD(date);

        // Create a deep copy of all payout options
        const updatedOptions = JSON.parse(JSON.stringify(payoutOptions));

        // Update the date of the selected payout
        updatedOptions[index] = {
          ...updatedOptions[index],
          date: formattedDate,
        };

        const isMaturityPayout = updatedOptions[index].isMaturity;
        let updatedMaturity = { ...maturity };

        // Update state immediately for better UI responsiveness
        setPayoutOptions(updatedOptions);

        // For custom payouts, we need to ensure they're in chronological order
        if (payoutSchedule === 'CUSTOM') {
          // Separate maturity and non-maturity options
          const maturityOption = updatedOptions.find((opt) => opt.isMaturity);
          const nonMaturityOptions = updatedOptions.filter(
            (opt) => !opt.isMaturity
          );

          // Sort non-maturity options by date
          nonMaturityOptions.sort(
            (a, b) => new Date(a.date) - new Date(b.date)
          );

          // Combine back with maturity at the end
          const sortedOptions = maturityOption
            ? [...nonMaturityOptions, maturityOption]
            : nonMaturityOptions;

          // Update the descriptions to reflect the new order
          const updatedWithDescriptions = sortedOptions.map((option, idx) => {
            if (option.isMaturity) {
              return {
                ...option,
                description: 'Last Payout:',
              };
            }

            return {
              ...option,
              description: `Payout ${idx + 1}`,
            };
          });

          // Update state with the sorted options
          setPayoutOptions(updatedWithDescriptions);

          // Call API to recalculate with the updated date
          apiCalculatePayout(
            isMaturityPayout
              ? updatedMaturity.endDateTime
              : maturity.endDateTime,
            amount,
            currency,
            productAPR,
            payoutSchedule,
            updatedWithDescriptions.map((opt) => ({
              date: formatDateForAPI(opt.date),
              isMaturity: opt.isMaturity,
            })),
            product.product_type,
            referenceRate,
            product.product_id,
            notionalCurrency
          )
            .then((response) => {
              if (response && response.payout_dates) {
                const apiMaturityDateTime = response.maturity_date_time;
                const apiMaturityDaysLeft = response.maturity_days_left;
                // Create a new array with updated amounts but preserved dates
                const finalOptions = updatedWithDescriptions.map((option) => {
                  // Find the corresponding payout in the API response
                  const apiDate = option.date.split('-').reverse().join('');
                  const payoutData = response.payout_dates.find((p) => {
                    const apiDateObj = new Date(p.payout_datetime);
                    const optionDateObj = new Date(option.date);

                    // Compare year, month, and day
                    return (
                      apiDateObj.getFullYear() ===
                        optionDateObj.getFullYear() &&
                      apiDateObj.getMonth() === optionDateObj.getMonth() &&
                      apiDateObj.getDate() === optionDateObj.getDate()
                    );
                  });

                  // If we found a matching payout, update the amounts
                  return {
                    ...option,
                    total: payoutData.payout_amount,
                    interest:
                      payoutData.payout_amount -
                      (option.isMaturity ? parseFloat(amount) : 0),
                    principal: option.isMaturity ? parseFloat(amount) : 0,
                  };
                });

                // Update state with the final options
                setPayoutOptions(finalOptions);
                setApiTotalPayout(response.total_payout || 0);

                // Notify parent component with updated values including maturity info
                if (onPayoutChange) {
                  onPayoutChange({
                    schedule: payoutSchedule,
                    options: finalOptions,
                    totalPayout: response.total_payout,
                    newAPR: response.new_apr,
                    calculatedRate: response.calculated_rate,
                    maturityDateTime: isMaturityPayout
                      ? apiMaturityDateTime
                      : maturity.endDateTime,
                    maturityDaysLeft: isMaturityPayout
                      ? apiMaturityDaysLeft
                      : maturity.daysLeft,
                    desiredFixedRate: response.desired_fixed_rate,
                    isDateChange: true,
                  });
                }
              }
            })
            .catch((error) => {
              console.error('Error updating payout date:', error);

              // If there's an error, restore the original dates
              const revertedOptions = updatedOptions.map((opt, i) => ({
                ...opt,
                date: originalDatesRef.current[i],
              }));

              setPayoutOptions(revertedOptions);
            });
        } else {
          // For non-custom payouts, just update the date without reordering
          setPayoutOptions(updatedOptions);

          // Call API to recalculate with the updated date
          apiCalculatePayout(
            isMaturityPayout
              ? updatedMaturity.endDateTime
              : maturity.endDateTime,
            amount,
            currency,
            productAPR,
            payoutSchedule,
            updatedOptions.map((opt) => ({
              date: opt.date,
              isMaturity: opt.isMaturity,
            })),
            product.product_type,
            undefined,
            product.product_id,
            notionalCurrency
          )
            .then((response) => {
              if (response && response.payout_dates) {
                // Create a new array with updated amounts but preserved dates
                const finalOptions = updatedOptions.map((option, idx) => {
                  // Find the corresponding payout in the API response
                  const apiDate = option.date.split('-').reverse().join('');
                  const payoutData = response.payout_dates.find((p) => {
                    const apiDateObj = new Date(p.payout_datetime);
                    const optionDateObj = new Date(option.date);

                    // Compare year, month, and day
                    return (
                      apiDateObj.getFullYear() ===
                        optionDateObj.getFullYear() &&
                      apiDateObj.getMonth() === optionDateObj.getMonth() &&
                      apiDateObj.getDate() === optionDateObj.getDate()
                    );
                  });

                  return {
                    ...option,
                    // Keep the original date
                    date: option.date,
                    // Update only the amounts
                    total: payoutData.payout_amount,
                    interest:
                      payoutData.payout_amount -
                      (option.isMaturity ? parseFloat(amount) : 0),
                    principal: option.isMaturity ? parseFloat(amount) : 0,
                  };
                });

                // Update state with the final options
                setPayoutOptions(finalOptions);
                setApiTotalPayout(response.total_payout || 0);

                // Notify parent component with updated values including maturity info
                if (onPayoutChange) {
                  onPayoutChange({
                    schedule: payoutSchedule,
                    options: finalOptions,
                    totalPayout: response.total_payout,
                    newAPR: response.new_apr,
                    calculatedRate: response.calculated_rate,
                    maturityDateTime: isMaturityPayout
                      ? updatedMaturity.endDateTime
                      : maturity.endDateTime,
                    maturityDaysLeft: isMaturityPayout
                      ? updatedMaturity.daysLeft
                      : maturity.daysLeft,
                  });
                }
              }
            })
            .catch((error) => {
              console.error('Error updating payout date:', error);

              // If there's an error, restore the original dates from the ref
              const revertedOptions = updatedOptions.map((opt, i) => ({
                ...opt,
                date: originalDatesRef.current[i],
              }));

              setPayoutOptions(revertedOptions);
            });
        }
      },
      [
        payoutOptions,
        amount,
        productAPR,
        maturity,
        payoutSchedule,
        onPayoutChange,
      ]
    );

    // Helper functions first
    const formatYMD = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      // Backend expects yyyy-MM-dd HH:mm format (no seconds)
      return `${year}-${month}-${day} 08:00`;
    };

    // Get monthly payout date range
    const getMonthlyPayoutDateRange = (dateStr) => {
      const date = new Date(dateStr);
      const month = date.getMonth();
      const year = date.getFullYear();

      // Min date: 1st day of the month
      const minDate = new Date(year, month, 1);

      // Max date: last day of the month
      const maxDate = new Date(year, month + 1, 0);

      return {
        min: formatYMD(minDate),
        max: formatYMD(maxDate),
      };
    };

    // Get quarterly payout date range
    const getQuarterlyPayoutDateRange = (dateStr) => {
      const date = new Date(dateStr);
      const month = date.getMonth();
      const year = date.getFullYear();

      // Determine the quarter (0-based)
      const quarter = Math.floor(month / 3);

      // Min date: 1st day of the first month in the quarter
      const minDate = new Date(year, quarter * 3, 1);

      // Max date: last day of the last month in the quarter
      const maxDate = new Date(year, (quarter + 1) * 3, 0);

      return {
        min: formatYMD(minDate),
        max: formatYMD(maxDate),
      };
    };

    // Add this helper function to get the minimum allowed date for a specific payout
    const getMinDateForPayout = (index, options) => {
      if (index === 0) {
        // For first payout, minimum is today
        return formatYMD(new Date());
      }

      // For subsequent payouts, minimum is the day after the previous payout
      const previousPayouts = options
        .filter((opt) => !opt.isMaturity)
        .slice(0, index);

      if (previousPayouts.length > 0) {
        const lastPayoutDate = new Date(
          previousPayouts[previousPayouts.length - 1].date
        );
        lastPayoutDate.setDate(lastPayoutDate.getDate() + 1);
        return formatYMD(lastPayoutDate);
      }

      return formatYMD(new Date());
    };

    // Add this helper function
    const formatDisplayDate = (dateStr) => {
      const date = new Date(dateStr);
      // format date to 17 Jun 2025 like this
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    };

    // Memoize min dates for custom payouts
    const minDates = useMemo(() => {
      return payoutOptions.map(
        (_, index) => new Date(getMinDateForPayout(index, payoutOptions))
      );
    }, [payoutOptions]);

    // Memoize date ranges for monthly/quarterly payouts
    const dateRanges = useMemo(() => {
      return payoutOptions.map((option) => {
        if (payoutSchedule === PAYOUT_SCHEDULES.MONTHLY) {
          const range = getMonthlyPayoutDateRange(option.date);
          return {
            min: new Date(range.min),
            max: new Date(range.max),
          };
        } else {
          const range = getQuarterlyPayoutDateRange(option.date);
          return {
            min: new Date(range.min),
            max: new Date(range.max),
          };
        }
      });
    }, [payoutOptions, payoutSchedule]);

    // Modify getWorkingDay function to handle new dates differently
    const getWorkingDay = (date, isNewDate = false) => {
      // Simplified version that just handles weekends
      const d = typeof date === 'string' ? new Date(date) : date;
      const day = d.getDay();

      // If it's a weekend (0 = Sunday, 6 = Saturday)
      if (day === 0 || day === 6) {
        const result = new Date(d);
        if (isNewDate) {
          // For new dates, move to next Monday
          result.setDate(d.getDate() + (day === 0 ? 1 : 2));
        } else {
          // For existing dates, move to previous Friday
          result.setDate(d.getDate() - (day === 0 ? 2 : 1));
        }
        return formatYMD(result);
      }

      // If it's a weekday, return the original date
      return typeof date === 'string' ? date : formatYMD(d);
    };

    // Wrap generatePayoutOptions in useCallback
    const generatePayoutOptions = useCallback(
      async (schedule) => {
        if (
          !schedule ||
          !product ||
          !maturity?.endDateTime ||
          daysLeft === undefined ||
          daysLeft === null ||
          !productAPR
        ) {
          console.log('Missing required values for payout calculation');
          return;
        }

        try {
          const totalAmount = parseFloat(amount);
          const investAmount = totalAmount;
          const maturityDate = new Date(maturity.endDateTime);
          const startDate = new Date(); // Today's date
          let dates = [];
          const maturityDateStr = formatYMD(maturityDate); // Moved here to be used by all cases

          switch (schedule) {
            case PAYOUT_SCHEDULES.MONTHLY:
              const monthsTotal = Math.floor(daysLeft / 30); // Calculate total months

              for (let i = 1; i < monthsTotal; i++) {
                const date = new Date(startDate);
                // Add months for regular payments
                date.setMonth(date.getMonth() + i);

                const dateToUse = formatYMD(date);
                const workingDate = getWorkingDay(dateToUse, true);

                // Skip if this date would be the same as maturity date
                if (workingDate !== maturityDateStr) {
                  dates.push({
                    date: workingDate,
                    isMaturity: false,
                  });
                }
              }

              // Add maturity date only if it's not already included
              if (!dates.some((d) => d.date === maturityDateStr)) {
                dates.push({
                  date: maturityDateStr,
                  isMaturity: true,
                });
              }
              break;

            case PAYOUT_SCHEDULES.QUARTERLY:
              const totalDays = Math.ceil(
                (maturityDate - startDate) / (1000 * 60 * 60 * 24)
              );
              const quarters = Math.floor(totalDays / 90); // Calculate number of quarters

              for (let i = 1; i < quarters; i++) {
                const date = new Date(startDate);
                // Add 3 months for each quarter
                date.setMonth(date.getMonth() + i * 3);

                const dateToUse = formatYMD(date);
                const workingDate = getWorkingDay(dateToUse, true);

                // Skip if this date would be the same as maturity date
                if (workingDate !== maturityDateStr) {
                  dates.push({
                    date: workingDate,
                    isMaturity: false,
                  });
                }
              }

              // Add maturity date only if it's not already included
              if (!dates.some((d) => d.date === maturityDateStr)) {
                dates.push({
                  date: maturityDateStr,
                  isMaturity: true,
                });
              }
              break;

            case PAYOUT_SCHEDULES.END_OF_TENURE:
            default:
              dates = [
                {
                  date: formatYMD(maturityDate),
                  isMaturity: true,
                },
              ];
              break;
          }

          // Sort dates in ascending order
          dates.sort((a, b) => new Date(a.date) - new Date(b.date));

          // Use mock API for Custom schedule, real API for others
          const response = await apiCalculatePayout(
            maturity.endDateTime,
            investAmount,
            currency,
            productAPR,
            schedule,
            dates,
            product.product_type,
            referenceRate,
            product.product_id,
            notionalCurrency
          );

          if (response && response.payout_dates) {
            // Sort the response payout dates to ensure they're in chronological order
            const sortedPayoutDates = [...response.payout_dates].sort(
              (a, b) => {
                // Convert DDMMYYYY to Date objects for comparison
                const dateA = new Date(
                  a.payout_datetime.slice(4),
                  parseInt(a.payout_datetime.slice(2, 4)) - 1, // Month (0-11)
                  a.payout_datetime.slice(0, 2) // Day
                );
                const dateB = new Date(
                  b.payout_datetime.slice(4),
                  parseInt(b.payout_datetime.slice(2, 4)) - 1,
                  b.payout_datetime.slice(0, 2)
                );
                return dateA - dateB;
              }
            );

            const options = sortedPayoutDates.map((payout, index) => {
              const isLastPayout = payout.is_Last_Payout || false;

              return {
                month: index + 1,
                date: `${payout.payout_datetime}`,
                description: isLastPayout
                  ? 'Last Payout:'
                  : schedule === PAYOUT_SCHEDULES.MONTHLY
                    ? `Month ${index + 1}`
                    : schedule === PAYOUT_SCHEDULES.QUARTERLY
                      ? `Quarter ${index + 1}`
                      : `Payout ${index + 1}`,
                principal: isLastPayout ? response.invest_amount : 0,
                interest:
                  payout.payout_amount -
                  (isLastPayout ? response.invest_amount : 0),
                total: payout.payout_amount,
                isMaturity: isLastPayout,
                isEditable: false,
              };
            });

            setPayoutOptions(options);
            setShowMonthlyOptions(true);
            setShowCustomOptions(schedule === 'CUSTOM');
            setApiTotalPayout(response.total_payout || 0);

            if (onPayoutChange) {
              onPayoutChange({
                schedule,
                options,
                totalPayout: response.total_payout,
                newAPR: response.new_apr,
                calculatedRate: response.calculated_rate,
                effectiveDate: response.value_date_time,
              });
            }
            setEffectiveDate(response.value_date_time);
          }
        } catch (error) {
          console.error('Error generating payout options:', error);
        }
      },
      [
        product,
        maturity,
        daysLeft,
        productAPR,
        amount,
        currency,
        selectedCurrency,
        referenceRate,
        onPayoutChange,
        formatYMD,
        getWorkingDay,
      ]
    );

    const parseEffectiveDate = useMemo(() => {
      if (!effectiveDate) return null;
      return new Date(effectiveDate);
    }, [effectiveDate]);

    const recalculatePayoutAmounts = useCallback(
      async (currentOptions) => {
        if (!product || !maturity?.endDateTime || !productAPR || !amount) {
          return;
        }

        try {
          setIsLoading(true);

          const response = await apiCalculatePayout(
            maturity.endDateTime,
            parseFloat(amount),
            currency,
            productAPR,
            payoutSchedule,
            currentOptions.map((opt) => ({
              date: formatDateForAPI(opt.date),
              isMaturity: opt.isMaturity,
            })),
            product.product_type,
            referenceRate,
            product.product_id,
            notionalCurrency
          );

          if (response && response.payout_dates) {
            // Update amounts but preserve dates
            const updatedOptions = currentOptions.map((option) => {
              const apiDateObj = new Date(option.date);
              const payoutData = response.payout_dates.find((p) => {
                const responseDate = new Date(p.payout_datetime);
                return (
                  responseDate.getFullYear() === apiDateObj.getFullYear() &&
                  responseDate.getMonth() === apiDateObj.getMonth() &&
                  responseDate.getDate() === apiDateObj.getDate()
                );
              });

              return {
                ...option,
                total: payoutData ? payoutData.payout_amount : option.total,
                interest: payoutData
                  ? payoutData.payout_amount -
                    (option.isMaturity ? parseFloat(amount) : 0)
                  : option.interest,
                principal: option.isMaturity ? parseFloat(amount) : 0,
              };
            });

            setPayoutOptions(updatedOptions);
            setApiTotalPayout(response.total_payout || 0);

            if (onPayoutChange) {
              onPayoutChange({
                schedule: payoutSchedule,
                options: updatedOptions,
                totalPayout: response.total_payout,
                newAPR: response.new_apr, // Keep new_apr for Annualised Return display
                calculatedRate: response.calculated_rate,
                desiredFixedRate: response.desired_fixed_rate, // Add this for the input field
                maturityDateTime:
                  response.maturity_date_time || maturity.endDateTime,
                maturityDaysLeft:
                  response.maturity_days_left || maturity.daysLeft,
              });
            }
          }
        } catch (error) {
          console.error('Error recalculating payout amounts:', error);
        } finally {
          setIsLoading(false);
        }
      },
      [
        product,
        maturity,
        productAPR,
        amount,
        currency,
        payoutSchedule,
        onPayoutChange,
        referenceRate,
      ]
    );

    const getPayoutScheduleOptions = () => {
      // Base options that are always available
      const baseOptions = [
        {
          id: 1,
          name: 'End of Tenure',
          subtitle: 'Single payment at maturity',
          value: PAYOUT_SCHEDULES.END_OF_TENURE,
        },
      ];

      // Check if tenure is at least 12 months (assuming daysLeft ≈ 365 for 12 months)
      const isTwelveMonthsOrMore = daysLeft >= 365;
      // Check if tenure is at least 3 months (assuming daysLeft ≈ 90 for 3 months)
      const isThreeMonthsOrMore = daysLeft >= 90;

      if (daysLeft >= 30) {
        baseOptions.push({
          id: 2,
          name: 'Monthly',
          subtitle: 'Regular monthly payments',
          value: PAYOUT_SCHEDULES.MONTHLY,
        });
      }

      // Only show Quarterly if tenure is at least 3 months
      if (isThreeMonthsOrMore) {
        baseOptions.push({
          id: 3,
          name: 'Quarterly',
          subtitle: 'Payments every 3 months',
          value: PAYOUT_SCHEDULES.QUARTERLY,
        });
      }

      baseOptions.push({
        id: 4,
        name: 'Custom',
        subtitle: 'Define your own schedule',
        value: 'CUSTOM',
      });

      return baseOptions;
    };

    // Update the handlePayoutScheduleChange function to show loading indicator
    const handlePayoutScheduleChange = useCallback(
      async (option) => {
        const newSchedule = option.value;
        setPayoutSchedule(newSchedule);

        // Set loading state to true before generating options
        setIsLoading(true);

        try {
          // Generate payout options based on the selected schedule
          await generatePayoutOptions(newSchedule);
        } catch (error) {
          console.error('Error changing payout schedule:', error);
        } finally {
          // Set loading state to false after generating options
          setIsLoading(false);
        }
      },
      [generatePayoutOptions]
    );

    // Fix the addCustomPayout function with minimal changes
    const addCustomPayout = async () => {
      if (!maturity || !amount || !productAPR) return;

      // Calculate interest and principal
      const totalAmount = parseFloat(amount);
      const investAmount = totalAmount;

      // Find the maturity payment (if any)
      const maturityPaymentIndex = payoutOptions.findIndex(
        (option) => option.isMaturity
      );
      const hasMaturity = maturityPaymentIndex !== -1;

      // Get the maturity date
      const maturityDate = new Date(maturity.endDateTime);

      // Get the date for the new payout
      let newPayoutDate;

      if (payoutOptions.length === 0) {
        // If no existing payouts, use today + 1 days as default
        const today = new Date();
        const nextDay = new Date();
        nextDay.setDate(today.getDate() + 1);
        const nextWorkingDay = getWorkingDay(nextDay, true);
        newPayoutDate = nextWorkingDay;
      } else {
        // Find the latest non-maturity payout date
        const nonMaturityPayouts = payoutOptions.filter(
          (option) => !option.isMaturity
        );

        if (nonMaturityPayouts.length === 0) {
          // If no non-maturity payouts, use today + 1 days
          const today = new Date(); // Fixed the syntax error here
          const nextDay = new Date();
          nextDay.setDate(today.getDate() + 1);
          const nextWorkingDay = getWorkingDay(nextDay, true);
          newPayoutDate = nextWorkingDay;
        } else {
          // Sort non-maturity payouts by date
          const sortedPayouts = [...nonMaturityPayouts].sort(
            (a, b) => new Date(a.date) - new Date(b.date)
          );

          // Get the latest non-maturity payout date
          const latestDate = new Date(
            sortedPayouts[sortedPayouts.length - 1].date
          );

          const nextDay = new Date(latestDate);
          nextDay.setDate(latestDate.getDate() + 1);
          // Set new date to 1 days after the latest date
          // newPayoutDate = new Date(latestDate);
          const nextWorkingDay = getWorkingDay(nextDay, true);
          newPayoutDate = nextWorkingDay;
        }
      }

      // Make sure the new date doesn't exceed maturity date
      if (newPayoutDate >= maturityDate) {
        // If it would exceed maturity, use a date 7 days before maturity
        newPayoutDate = new Date(maturityDate);
        newPayoutDate.setDate(maturityDate.getDate() - 7);

        // If that's still not valid (e.g., very short maturity), use tomorrow
        if (newPayoutDate <= new Date()) {
          newPayoutDate = new Date();
          newPayoutDate.setDate(newPayoutDate.getDate() + 1);
        }
      }

      // Format the date
      const formattedDate = formatYMD(newPayoutDate);

      // Create the new payout
      const newPayout = {
        month: payoutOptions.length + 1,
        date: formattedDate,
        description: `Payout ${payoutOptions.length + 1}`,
        principal: 0,
        interest: 0,
        total: 0,
        isMaturity: false,
        isEditable: true,
      };

      // Add the new payout before the maturity payment if it exists
      let updatedOptions;
      if (hasMaturity) {
        updatedOptions = [
          ...payoutOptions.slice(0, maturityPaymentIndex),
          newPayout,
          ...payoutOptions.slice(maturityPaymentIndex),
        ];
      } else {
        updatedOptions = [...payoutOptions, newPayout];
      }

      // Update month numbers for all payouts
      updatedOptions = updatedOptions.map((option, index) => ({
        ...option,
        month: index + 1,
      }));
      console.log(
        'Updated Options after adding custom payout:',
        formattedDate,
        maturity.endDateTime
      );

      // Update state
      setPayoutOptions(updatedOptions);

      // Call API to calculate payout amounts
      try {
        const response = await apiCalculatePayout(
          maturity.endDateTime,
          investAmount,
          currency,
          productAPR,
          'CUSTOM',
          updatedOptions.map((opt) => ({
            date: opt.date,
            isMaturity: opt.isMaturity,
          })),
          product.product_type,
          referenceRate,
          product.product_id,
          notionalCurrency
        );

        if (response && response.payout_dates) {
          // Map API response back to our options
          const updatedWithAmounts = updatedOptions.map((option) => {
            const apiDate = option.date.split('-').reverse().join('');
            const payoutData = response.payout_dates.find(
              (p) => p.date === apiDate
            );

            if (payoutData) {
              return {
                ...option,
                total: payoutData.payout_amount,
                interest:
                  payoutData.payout_amount -
                  (option.isMaturity ? investAmount : 0),
                principal: option.isMaturity ? investAmount : 0,
              };
            }
            return option;
          });

          setPayoutOptions(updatedWithAmounts);
          setApiTotalPayout(response.total_payout || 0);

          // Notify parent component
          if (onPayoutChange) {
            onPayoutChange({
              schedule: 'CUSTOM',
              options: updatedWithAmounts,
              totalPayout: response.total_payout,
              newAPR: response.new_apr,
              calculatedRate: response.calculated_rate,
              desiredFixedRate: response.desired_fixed_rate,
            });
          }
        }
      } catch (error) {
        console.error('Error calculating custom payout:', error);
        // Keep the basic payout if API fails
      }
    };

    // Update the addMaturityPayout function to work like addCustomPayout but for maturity payments
    const addMaturityPayout = async () => {
      // Calculate interest and principal
      const totalAmount = parseFloat(amount);
      const investAmount = totalAmount;

      // Get the date for maturity payout (use the actual maturity date from the product)
      const maturityDate = formatYMD(new Date(maturity.endDateTime));

      // Remove any existing maturity payment
      const regularPayouts = payoutOptions.filter(
        (option) => !option.isMaturity
      );

      // Create the maturity payment
      const maturityPayment = {
        month: regularPayouts.length + 1,
        date: maturityDate,
        description: 'Last Payout',
        principal: investAmount,
        interest: 0, // Will be calculated by API
        total: investAmount, // Initial value, will be updated by API
        isMaturity: true,
        isEditable: true,
      };

      // Combine regular payouts with the new maturity payment
      const updatedOptions = [...regularPayouts, maturityPayment];

      // Update state immediately for better UI responsiveness
      setPayoutOptions(updatedOptions);

      // Call API to calculate actual payout amounts
      try {
        const response = await apiCalculatePayout(
          maturity.endDateTime,
          investAmount,
          currency,
          productAPR,
          'CUSTOM',
          updatedOptions.map((opt) => ({
            date: opt.date,
            isMaturity: opt.isMaturity,
          })),
          product.product_type,
          referenceRate,
          product.product_id,
          notionalCurrency
        );

        if (response && response.payout_dates) {
          // Map API response back to our options
          const updatedWithAmounts = updatedOptions.map((option) => {
            const apiDate = option.date.split('-').reverse().join('');
            const payoutData = response.payout_dates.find(
              (p) => p.date === apiDate
            );

            if (payoutData) {
              return {
                ...option,
                total: payoutData.payout_amount,
                interest:
                  payoutData.payout_amount -
                  (option.isMaturity ? investAmount : 0),
                principal: option.isMaturity ? investAmount : 0,
              };
            }
            return option;
          });

          setPayoutOptions(updatedWithAmounts);
          setApiTotalPayout(response.total_payout || 0);

          // Notify parent component
          if (onPayoutChange) {
            onPayoutChange({
              schedule: 'CUSTOM',
              options: updatedWithAmounts,
              totalPayout: response.total_payout,
              newAPR: response.new_apr,
              calculatedRate: response.calculated_rate,
              desiredFixedRate: response.desired_fixed_rate,
            });
          }
        }
      } catch (error) {
        console.error('Error calculating maturity payment:', error);
        // Keep the basic maturity payment if API fails
      }
    };

    // Update the removeCustomPayout function to properly handle maturity payment removal
    const removeCustomPayout = (index) => {
      // Get the payout to be removed
      const payoutToRemove = payoutOptions[index];

      // Create updated options by removing the selected payout
      let updatedOptions = payoutOptions.filter((_, i) => i !== index);

      // If we removed all payouts, just set empty array
      if (updatedOptions.length === 0) {
        setPayoutOptions([]);
      } else {
        // Otherwise update with the new options
        setPayoutOptions(updatedOptions);
      }

      // Notify parent component
      if (onPayoutChange) {
        onPayoutChange({
          schedule: payoutSchedule,
          options: updatedOptions,
        });
      }
    };

    // Fix the hasMaturityPayment function
    const hasMaturityPayment = () => {
      // Simply check if any option has isMaturity set to true
      return payoutOptions.some((option) => option.isMaturity);
    };

    // Update the renderCustomPayouts function to properly use animation
    // const renderCustomPayouts = () => {
    //   if (!showCustomOptions) return null;

    //   // Get the maturity date as a Date object
    //   const maturityDate = maturity ? new Date(maturity.endDateTime) : null;

    //   // Create a max date that's one day before maturity
    //   const maxDate = maturityDate ? new Date(maturityDate.getTime()) : null;
    //   if (maxDate) {
    //     maxDate.setDate(maxDate.getDate() - 1);
    //   }

    //   return (
    //     <div className="col-12">
    //       <div className="card card-bordered">
    //         <div className="card-inner">
    //           <div className="d-flex justify-content-between align-items-center mb-3">
    //             <h5 className="card-title mb-0">Custom Payout Plan</h5>
    //             <button
    //               className="btn btn-sm btn-primary"
    //               onClick={addCustomPayout}
    //             >
    //               <em className="icon ni ni-plus"></em>
    //               <span>Add Payout</span>
    //             </button>
    //           </div>

    //           {/* {isLoading && (
    //             <div className="text-center py-3">
    //               <div className="spinner-border text-primary" role="status">
    //                 <span className="visually-hidden">Loading...</span>
    //               </div>
    //               <p className="mt-2 text-muted">Calculating payouts...</p>
    //             </div>
    //           )} */}

    //           <div className="payout-section">
    //             {payoutOptions.length === 0 ? (
    //               <div className="text-center py-4 text-muted">
    //                 No custom payouts defined. Click "Add Payout" to create one.
    //               </div>
    //             ) : (
    //               <TransitionGroup className="payout-list">
    //                 {payoutOptions.map((option, index) => (
    //                   <CSSTransition
    //                     key={option.isMaturity ? 'maturity' : `payout-${option.date}`}
    //                     timeout={300}
    //                     classNames="payout-item"
    //                   >
    //                     <div
    //                       className={`payout-row ${option.animateReorder ? 'reordering' : ''}`}
    //                     >
    //                       <div className="payout-label">
    //                         {option.isMaturity ? 'Last Payout:' : `Payout ${index + 1}`}
    //                       </div>
    //                       <div className="payout-content">
    //                         <div className="form-control-wrap">
    //                           <MemoizedDatePicker
    //                             selected={option.date}
    //                             onChange={(date) => handleDateChange(index, date)}
    //                             dateFormat="dd MMM yyyy"
    //                             minDate={minDates[index]}
    //                             maxDate={option.isMaturity ? maturityDate : maxDate}
    //                             placeholderText="Select date"
    //                             index={index}
    //                             disabled={isLoading}
    //                           />
    //                         </div>
    //                         <div className="payout-value text-info">
    //                           {showPrice(
    //                             option.total,
    //                             currency === 'usd' ? selectedCurrency : currency.toUpperCase(),
    //                             null,
    //                             true
    //                           )}
    //                         </div>

    //                         <div className="payout-action">
    //                           <button
    //                             className="btn btn-icon btn-outline-danger btn-sm"
    //                             onClick={() => removeCustomPayout(index)}
    //                             disabled={isLoading}
    //                           >
    //                             <em className="icon ni ni-trash"></em>
    //                           </button>
    //                         </div>
    //                       </div>
    //                     </div>
    //                   </CSSTransition>
    //                 ))}
    //               </TransitionGroup>
    //             )}

    //             {payoutOptions.length > 0 && !hasMaturityPayment() && (
    //               <div className="mt-3">
    //                 <button
    //                   className="btn btn-warning btn-sm"
    //                   onClick={addMaturityPayout}
    //                 >
    //                   <em className="icon ni ni-calendar-alt"></em>
    //                   <span>Add Maturity Payment</span>
    //                 </button>
    //                 <div className="form-note mt-2">
    //                   A maturity payment will be required to receive your principal amount
    //                 </div>
    //               </div>
    //             )}
    //           </div>
    //         </div>
    //       </div>
    //     </div>
    //   );
    // };

    const formatPayoutAmount = (amount) => {
      const displayCurrency =
        currency === 'usd' ? selectedCurrency : currency.toUpperCase();

      // // Apply truncate logic only for 1-day PST product
      // if (product?.product_id === ONE_DAY_PS_PRODUCT_ID) {
      //   return truncateAmount(amount, displayCurrency);
      // }

      // // Existing behaviour for all other products
      // return showPrice(amount, displayCurrency, null, true);
      return groupInteger(truncateAmount(amount, displayCurrency));
    };

    const renderCustomPayouts = () => {
      if (!showCustomOptions) return null;

      // Get the maturity date as a Date object
      const maturityDate = maturity ? new Date(maturity.endDateTime) : null;

      // Create a max date that's one day before maturity
      const maxDate = maturityDate ? new Date(maturityDate.getTime()) : null;
      if (maxDate) {
        maxDate.setDate(maxDate.getDate() - 1);
      }

      return (
        <div className="col-12">
          <div className="card card-bordered">
            <div className="payout-card-inner">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Custom Payout Plan</h5>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={addCustomPayout}
                  disabled={isLoading}
                >
                  <em className="icon ni ni-plus"></em>
                  <span>Add Payout</span>
                </button>
              </div>

              {isLoading && (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Calculating payouts...</p>
                </div>
              )}

              <div className="payout-section">
                {/* Header row for column labels */}
                <div className="payout-header-row px-4 py-2">
                  <div className="payout-header-label">Period</div>
                  <div className="payout-header-content">
                    <div className="payout-header-date">Date</div>
                    <div className="payout-header-amount">Amount</div>
                  </div>
                </div>

                {payoutOptions.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    No custom payouts defined. Click "Add Payout" to create one.
                  </div>
                ) : (
                  <TransitionGroup className="payout-list">
                    {payoutOptions.map((option, index) => (
                      <CSSTransition
                        key={
                          option.isMaturity
                            ? 'maturity'
                            : `payout-${option.date}`
                        }
                        timeout={300}
                        classNames="payout-item"
                      >
                        <div
                          className={`payout-row px-4 ${
                            option.animateReorder ? 'reordering' : ''
                          }`}
                        >
                          <div className="payout-label">
                            {option.isMaturity
                              ? 'Last Payout:'
                              : `Payout ${index + 1}`}
                          </div>
                          <div className="custom-payout-content">
                            <div className="form-control-wrap">
                              <MemoizedDatePicker
                                selected={option.date}
                                onChange={(date) =>
                                  handleDateChange(index, date)
                                }
                                dateFormat="dd MMM yyyy"
                                minDate={
                                  option.isMaturity && parseEffectiveDate
                                    ? new Date(
                                        parseEffectiveDate.getTime() +
                                          24 * 60 * 60 * 1000
                                      )
                                    : minDates[index]
                                }
                                maxDate={
                                  option.isMaturity ? maturityDate : maxDate
                                }
                                placeholderText="Select date"
                                className="date-picker"
                                disabled={isLoading}
                                index={index}
                                effectiveDate={effectiveDate}
                              />
                            </div>
                            <div className="payout-value">
                              {groupInteger(
                                truncateAmount(
                                  option.total,
                                  currency === 'usd'
                                    ? notionalCurrency || selectedCurrency
                                    : currency.toUpperCase()
                                )
                              )}
                            </div>
                            <div className="payout-action">
                              <button
                                className="btn btn-icon btn-outline-danger btn-sm"
                                onClick={() => removeCustomPayout(index)}
                                disabled={isLoading}
                              >
                                <em className="icon ni ni-trash"></em>
                              </button>
                            </div>
                          </div>
                        </div>
                      </CSSTransition>
                    ))}
                  </TransitionGroup>
                )}

                {payoutOptions.length > 0 && !hasMaturityPayment() && (
                  <div className="mt-3">
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={addMaturityPayout}
                      disabled={isLoading}
                    >
                      <em className="icon ni ni-calendar-alt"></em>
                      <span>Add Maturity Payment</span>
                    </button>
                    <div className="form-note mt-2">
                      A maturity payment will be required to receive your
                      principal amount
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Add the renderMonthlyPayouts function
    // const renderMonthlyPayouts = () => {
    //   if (!showMonthlyOptions || showCustomOptions) return null;

    //   return (
    //     <div className="col-12">
    //       <div className="card card-bordered">
    //         <div className="card-inner">
    //           <div className="d-flex justify-content-between align-items-center mb-3">
    //             <h5 className="card-title mb-0">
    //               {payoutSchedule === PAYOUT_SCHEDULES.MONTHLY ? 'Monthly' : 'Quarterly'} Payout Plan
    //             </h5>
    //           </div>

    //           <div className="payout-section">
    //             {payoutOptions.map((option, index) => (
    //               <div className="payout-row" key={index}>
    //                 <div className="payout-label">
    //                   {option.isMaturity ? 'Last Payout:' : option.description}
    //                 </div>
    //                 <div className="payout-content">
    //                   <div className="form-control-wrap">
    //                     {!option.isMaturity ? (
    //                       <MemoizedDatePicker
    //                         selected={option.date}
    //                         onChange={(date) => handleDateChange(index, date)}
    //                         dateFormat="dd MMM yyyy"
    //                         minDate={dateRanges[index]?.min}
    //                         maxDate={dateRanges[index]?.max}
    //                         disabled={option.isMaturity}
    //                         placeholderText="Select date"
    //                         index={index}
    //                       />
    //                     ) : (
    //                       <span className="payout-date">
    //                         {formatDisplayDate(option.date)}
    //                       </span>
    //                     )}
    //                   </div>
    //                   <div className="payout-value text-info">
    //                     {showPrice(
    //                       option.total,
    //                       currency === 'usd' ? selectedCurrency : currency.toUpperCase(),
    //                       null,
    //                       true
    //                     )}
    //                   </div>
    //                 </div>
    //               </div>
    //             ))}
    //           </div>
    //         </div>
    //       </div>
    //     </div>
    //   );
    // };

    const renderMonthlyPayouts = () => {
      if (!showMonthlyOptions || showCustomOptions) return null;

      return (
        <div className="col-12">
          <div className="card card-bordered">
            <div className="payout-card-inner">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">
                  {payoutSchedule === PAYOUT_SCHEDULES.MONTHLY
                    ? 'Monthly'
                    : 'Quarterly'}{' '}
                  Payout Plan
                </h5>
              </div>

              <div className="payout-section ">
                {/* Header row for column labels */}
                <div className="payout-header-row px-4 py-2">
                  <div className="payout-header-label">Month</div>
                  <div className="payout-header-content">
                    <div className="payout-header-date">Date</div>
                    <div className="payout-header-amount">Amount</div>
                  </div>
                </div>

                {payoutOptions.map((option, index) => (
                  <div
                    className={`payout-row px-4 ${
                      option.isMaturity ? 'maturity-payout' : ''
                    }`}
                    key={index}
                  >
                    <div className="payout-label">
                      {option.isMaturity ? 'Last Payout:' : option.description}
                    </div>
                    <div className="payout-content">
                      <div className="form-control-wrap">
                        {!option.isMaturity ? (
                          <MemoizedDatePicker
                            selected={option.date}
                            onChange={(date) => handleDateChange(index, date)}
                            dateFormat="dd MMM yyyy"
                            minDate={dateRanges[index]?.min}
                            maxDate={dateRanges[index]?.max}
                            disabled={option.isMaturity}
                            placeholderText="Select date"
                            className="date-picker"
                            index={index}
                          />
                        ) : (
                          <span className="payout-date">
                            {formatDisplayDate(option.date)}
                          </span>
                        )}
                      </div>
                      <div className="payout-value">
                        {showPrice(
                          option.total,
                          currency === 'usd'
                            ? notionalCurrency || selectedCurrency
                            : currency.toUpperCase(),
                          null,
                          true
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Add a new function to render End of Tenure payout
    // const renderEndOfTenurePayout = () => {
    //   if (payoutSchedule !== 'End of Tenure' && payoutSchedule !== PAYOUT_SCHEDULES.END_OF_TENURE) return null;

    //   // Don't show anything if amount is not provided
    //   if (!amount || parseFloat(apiTotalPayout) <= 0) {
    //     return (
    //       <div className="col-12">
    //         <div className="card card-bordered">
    //           <div className="card-inner text-center py-4">
    //             <p className="text-muted">Please enter an investment amount to see payout details.</p>
    //           </div>
    //         </div>
    //       </div>
    //     );
    //   }

    //   // Calculate maturity date and amount
    //   const maturityDate = maturity ? formatYMD(new Date(maturity.endDateTime)) : '';
    //   const totalAmount = parseFloat(apiTotalPayout);
    //   // const interestAmount = (totalAmount * productAPR * daysLeft) / (100 * 365);
    //   const totalPayout = totalAmount

    //   return (
    //     <div className="col-12">
    //       <div className="card card-bordered">
    //         <div className="card-inner">
    //           <div className="d-flex justify-content-between align-items-center mb-3">
    //             <h5 className="card-title mb-0">End of Tenure Payout</h5>
    //           </div>

    //           <div className="payout-section">
    //             <div className="payout-row">
    //               <div className="payout-label">Maturity Payout:</div>
    //               <div className="payout-content">
    //                 <div className="form-control-wrap">
    //                   <span className="payout-date">
    //                     {formatDisplayDate(maturityDate)}
    //                   </span>
    //                 </div>
    //                 <div className="payout-value text-info">
    //                   {showPrice(
    //                     totalPayout,
    //                     currency === 'usd' ? selectedCurrency : currency.toUpperCase(),
    //                     null,
    //                     true
    //                   )}
    //                 </div>
    //               </div>
    //             </div>
    //           </div>

    //           <div className="mt-4">
    //             <div className="data-item">
    //               <div className="data-col">
    //                 <span className="data-label">Total Payout</span>
    //                 <span className="data-value text-primary">
    //                   {showPrice(
    //                     totalPayout,
    //                     currency === 'usd' ? selectedCurrency : currency.toUpperCase(),
    //                     null,
    //                     true
    //                   )}
    //                 </span>
    //               </div>
    //             </div>
    //           </div>
    //         </div>
    //       </div>
    //     </div>
    //   );
    // };

    const renderEndOfTenurePayout = () => {
      if (
        payoutSchedule !== 'End of Tenure' &&
        payoutSchedule !== PAYOUT_SCHEDULES.END_OF_TENURE
      )
        return null;

      // Don't show anything if amount is not provided
      if (!amount || parseFloat(apiTotalPayout) <= 0) {
        return (
          <div className="col-12">
            <div className="card card-bordered">
              <div className="payout-card-inner text-center py-4">
                <p className="text-muted">
                  Please enter an investment amount to see payout details.
                </p>
              </div>
            </div>
          </div>
        );
      }

      // Calculate maturity date and amount
      const maturityDate = maturity
        ? formatYMD(new Date(maturity.endDateTime))
        : '';
      const totalAmount = parseFloat(apiTotalPayout);

      let redemptionAmount;
      let displayCurrency;

      // totalAmount (apiTotalPayout) is already in notional/product currency from backend
      if (currency.toUpperCase() === 'USD') {
        redemptionAmount = totalAmount;
        displayCurrency = notionalCurrency || selectedCurrency || 'USD';
      } else {
        redemptionAmount = totalAmount;
        displayCurrency = currency.toUpperCase();
      }

      return (
        <div className="col-12">
          <div className="card card-bordered">
            <div className="payout-card-inner">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">End of Tenure Payout</h5>
              </div>

              <div className="payout-section">
                <div className="payout-header-row px-4 py-2">
                  <div className="payout-header-label">Period</div>
                  <div className="payout-header-content">
                    <div className="payout-header-date">Date</div>
                    <div className="payout-header-amount">Amount</div>
                  </div>
                </div>

                <div className="payout-row px-4 maturity-payout">
                  <div className="payout-label">Maturity Payout:</div>
                  <div className="payout-content">
                    <div className="form-control-wrap">
                      <span
                        className="payout-date"
                        style={{ marginTop: '10px' }}
                      >
                        {formatDisplayDate(maturityDate)}
                      </span>
                    </div>
                    <div className="payout-value">
                      {
                        /*showPrice(
                        redemptionAmount,
                        currency === 'usd'
                          ? selectedCurrency
                          : currency.toUpperCase(),
                        null,
                        true
                      )*/
                        formatPayoutAmount(redemptionAmount)
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-medium">Total Payout</span>
                  <span className="payout-value text-primary fw-bold">
                    {formatPayoutAmount(redemptionAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Main render function
    return (
      <div>
        <div className="nk-iv-scheme-details">
          <div className="row g-3">
            {/* Payout Schedule Dropdown */}
            {product && (
              <div className="col-12">
                <div className="form-group w-50">
                  <label className="form-label" htmlFor="payoutSchedule">
                    Payout Schedule
                  </label>
                  <div className="form-control-wrap">
                    <DropDown
                      id="payoutSchedule"
                      options={getPayoutScheduleOptions()}
                      selectedOption={getPayoutScheduleOptions().find(
                        (opt) => opt.value === payoutSchedule
                      )}
                      onChange={handlePayoutScheduleChange}
                      placeholder="Select payout schedule"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Show message when no payout schedule is selected */}
            {!payoutSchedule ? (
              <div className="col-12">
                <div className="card card-bordered">
                  <div className="card-inner text-center py-4">
                    <p className="text-muted">
                      Please select a payout schedule to see payout details.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Check if amount is provided when payout schedule is selected */}
                {!amount || parseFloat(amount) <= 0 ? (
                  <div className="col-12">
                    <div className="card card-bordered">
                      <div className="card-inner text-center py-4">
                        <p className="text-muted">
                          Please enter an investment amount to see payout
                          details.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* End of Tenure Payout Display */}
                    {(payoutSchedule === 'End of Tenure' ||
                      payoutSchedule === PAYOUT_SCHEDULES.END_OF_TENURE) &&
                      renderEndOfTenurePayout()}

                    {/* Only show other payout plans if not End of Tenure */}
                    {payoutSchedule !== 'End of Tenure' &&
                      payoutSchedule !== PAYOUT_SCHEDULES.END_OF_TENURE && (
                        <>
                          {/* Monthly/Quarterly Payout Display */}
                          {showMonthlyOptions && renderMonthlyPayouts()}

                          {/* Custom Payout Options */}
                          {showCustomOptions && renderCustomPayouts()}
                        </>
                      )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
      prevProps.product?.id === nextProps.product?.id &&
      prevProps.amount === nextProps.amount &&
      prevProps.daysLeft === nextProps.daysLeft &&
      prevProps.productAPR === nextProps.productAPR &&
      prevProps.currency === nextProps.currency &&
      prevProps.selectedCurrency === nextProps.selectedCurrency &&
      prevProps.maturity?.endDateTime === nextProps.maturity?.endDateTime &&
      prevProps.referenceRate === nextProps.referenceRate &&
      prevProps.notionalCurrency === nextProps.notionalCurrency
    );
  }
);

export default MultiInterestPayout;
